import React from 'react';
import { Link } from 'react-router-dom';
import Button from '../common/Button';
import { Modal, ModalContent } from '../common';
import { Card } from '../common/Card';
import { FormField, Input, Select } from '../common/Form';
import Captcha from '../common/Captcha';
import VerificationModal from '../common/VerificationModal';
import { CAPTCHA_CONFIG } from '../../config/captcha';

const AuthForm = ({
  mode,
  form,
  switchTo,
}) => {
  const isLogin = mode === 'login';

  return (
    <>
      <Card variant="default" size="medium" className="w-full max-w-md">
        <h2 className="text-2xl font-bold mb-4 text-center">
          {isLogin ? 'Login to Shuttlr' : 'Register for Shuttlr'}
        </h2>

        {form.error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {form.error}
          </div>
        )}

        {form.success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {form.success}
          </div>
        )}

        {!isLogin && Object.keys(form.validationErrors).length > 0 && (
          <div className="bg-red-50 border-l-4 border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 mb-2">
                  Please fix the following errors:
                </h3>
                <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                  {form.validationErrors.first_name && <li>{form.validationErrors.first_name}</li>}
                  {form.validationErrors.last_name && <li>{form.validationErrors.last_name}</li>}
                  {form.validationErrors.gender && <li>{form.validationErrors.gender}</li>}
                  {form.validationErrors.email && <li>{form.validationErrors.email}</li>}
                  {form.validationErrors.password && <li>{form.validationErrors.password}</li>}
                </ul>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={form.handleSubmit}>
          {!isLogin && (
            <>
              <FormField label="First Name" required>
                <Input
                  type="text"
                  name="first_name"
                  placeholder="Enter your first name"
                  value={form.formData.first_name}
                  onChange={form.handleInputChange}
                  error={form.validationErrors.first_name}
                />
              </FormField>
              <FormField label="Last Name" required>
                <Input
                  type="text"
                  name="last_name"
                  placeholder="Enter your last name"
                  value={form.formData.last_name}
                  onChange={form.handleInputChange}
                  error={form.validationErrors.last_name}
                />
              </FormField>
              <FormField
                label="Competition category"
                required
                helperText="Used for matchmaking, draws, and Elo ratings."
              >
                <Select
                  name="gender"
                  value={form.formData.gender}
                  onChange={form.handleInputChange}
                  error={form.validationErrors.gender}
                  placeholder="Select competition category"
                  options={[
                    { value: 'male', label: "Men's" },
                    { value: 'female', label: "Women's" },
                  ]}
                />
              </FormField>
            </>
          )}

          <FormField label="Email" required>
            <Input
              type="email"
              name="email"
              placeholder="Enter your email address"
              value={form.formData.email}
              onChange={form.handleInputChange}
              onFocus={form.revealCaptchaField}
              error={form.validationErrors.email}
            />
          </FormField>

          <FormField
            label="Password"
            required
            helperText={
              !isLogin
                ? 'Password must be at least 8 characters and include uppercase, lowercase, number, and special character'
                : undefined
            }
          >
            <Input
              type="password"
              name="password"
              placeholder="Enter your password"
              value={form.formData.password}
              onChange={form.handleInputChange}
              onFocus={form.revealCaptchaField}
              error={form.validationErrors.password}
            />
          </FormField>

          {!CAPTCHA_CONFIG.SKIP_UI && form.showCaptchaField && (
            <div className="mb-6">
              {CAPTCHA_CONFIG.IS_CONFIGURED ? (
                <Captcha
                  ref={form.captchaRef}
                  siteKey={CAPTCHA_CONFIG.SITE_KEY}
                  onVerify={form.handleCaptchaVerify}
                  onExpire={form.handleCaptchaExpire}
                  onError={form.handleCaptchaError}
                  theme={CAPTCHA_CONFIG.THEME}
                  size={CAPTCHA_CONFIG.SIZE}
                />
              ) : (
                <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  Sign-in protection is not configured. Add{' '}
                  <code className="text-xs">REACT_APP_RECAPTCHA_SITE_KEY</code> in hosting
                  settings and redeploy.
                </div>
              )}
              {form.captchaError && (
                <div className="mt-2 text-sm text-red-600">{form.captchaError}</div>
              )}
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Button
                variant="primary"
                type="submit"
                disabled={form.submitting}
                className="w-full"
              >
                {form.submitting ? 'Processing...' : (isLogin ? 'Sign In' : 'Register')}
              </Button>
            </div>

            {isLogin && (
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={form.handleForgotPassword}
                  className="text-blue-600 hover:text-blue-700 focus:outline-none"
                >
                  Forgot password?
                </button>
                <Button
                  as={Link}
                  to={switchTo || '/signup'}
                  variant="link"
                  className="text-sm"
                >
                  Need an account?
                </Button>
              </div>
            )}

            {!isLogin && (
              <div className="flex items-center justify-end">
                <Button
                  as={Link}
                  to={switchTo || '/login'}
                  variant="link"
                  className="text-sm"
                >
                  Already have an account?
                </Button>
              </div>
            )}
            <p className="text-center text-xs text-text-muted">
              {isLogin ? 'See how we use your information in our ' : 'By creating an account you acknowledge our '}
              <Link to="/privacy" className="text-primary underline-offset-4 hover:underline">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </form>

        {process.env.REACT_APP_ENABLE_GOOGLE_AUTH === 'true' && (
          <div className="mt-4">
            <div className="relative flex items-center">
              <div className="flex-grow border-t border-border" />
              <span className="mx-3 flex-shrink text-xs text-text-muted">or</span>
              <div className="flex-grow border-t border-border" />
            </div>
            <button
              type="button"
              onClick={form.handleGoogleSignIn}
              disabled={form.submitting}
              className="mt-3 flex w-full items-center justify-center gap-3 rounded-md border border-border bg-surface px-4 py-2 text-sm font-medium text-text-primary shadow-sm transition-colors hover:bg-background disabled:opacity-50"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
              </svg>
              Continue with Google
            </button>
            {isLogin && (
              <p className="mt-2 text-center text-xs text-text-muted">
                Use the same email as your Shuttlr account to link automatically.
              </p>
            )}
          </div>
        )}
      </Card>

      {isLogin && (
        <Modal
          isOpen={form.showForgotPasswordModal}
          onClose={() => form.setShowForgotPasswordModal(false)}
          closeOnBackdropClick={!form.isResettingPassword}
        >
          <ModalContent size="small" constrainHeight={false}>
            <h3 className="text-lg font-semibold text-text-primary mb-2">Reset your password</h3>
            <p className="text-sm text-text-secondary mb-4">
              Enter the email address associated with your account and we&apos;ll send you a password reset link.
            </p>

            {form.forgotPasswordError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-3 text-sm">
                {form.forgotPasswordError}
              </div>
            )}

            {form.forgotPasswordSuccess && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-2 rounded mb-3 text-sm">
                {form.forgotPasswordSuccess}
              </div>
            )}

            <form onSubmit={form.handleForgotPasswordSubmit} className="space-y-4">
              <FormField label="Email" required>
                <Input
                  type="email"
                  name="forgot_email"
                  placeholder="Enter your email address"
                  value={form.forgotPasswordEmail}
                  onChange={(e) => form.setForgotPasswordEmail(e.target.value)}
                />
              </FormField>

              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => form.setShowForgotPasswordModal(false)}
                  disabled={form.isResettingPassword}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={form.isResettingPassword}
                >
                  {form.isResettingPassword ? 'Sending...' : 'Send reset link'}
                </Button>
              </div>
            </form>
          </ModalContent>
        </Modal>
      )}

      {form.showVerificationModal && (
        <VerificationModal
          email={form.verificationEmail}
          onClose={() => form.setShowVerificationModal(false)}
        />
      )}
    </>
  );
};

export default AuthForm;
